import React, { memo } from 'react';
import classNames from 'classnames';
import styled, { css } from 'styled-components';
import { spacing, deviceSizes } from '../../style';
import { LinkProps } from '../link/link';
import {
  CssUnitsAsResponsiveObject,
  getCssRuleWithResponsiveCalculation,
} from '../../utils/get-css-rule-with-responsive-calc';
import { mapSpacingPropsToValues, withSpacing } from '../../utils/with-spacing';
import { borderRadiusOptions } from './style';

type SpacingValues = keyof typeof spacing | { [key in keyof typeof deviceSizes]?: keyof typeof spacing };

export interface SpacingProps {
  marginTop?: SpacingValues;
  margin?: SpacingValues;
  marginBottom?: SpacingValues;
  marginLeft?: SpacingValues;
  marginRight?: SpacingValues;
  marginVertical?: SpacingValues;
  marginHorizontal?: SpacingValues;
  padding?: SpacingValues;
  paddingTop?: SpacingValues;
  paddingBottom?: SpacingValues;
  paddingRight?: SpacingValues;
  paddingLeft?: SpacingValues;
  paddingVertical?: SpacingValues;
  paddingHorizontal?: SpacingValues;
}

export interface PositionProps {
  position?: CssUnitsAsResponsiveObject<'static' | 'relative' | 'absolute' | 'fixed' | 'sticky'>;
  top?: CssUnitsAsResponsiveObject<string | number>;
  bottom?: CssUnitsAsResponsiveObject<string | number>;
  left?: CssUnitsAsResponsiveObject<string | number>;
  right?: CssUnitsAsResponsiveObject<string | number>;
}

export interface BoxProps extends SpacingProps, PositionProps {
  componentClass?: any;
  className?: string;
  id?: string;
  dir?: 'ltr' | 'rtl' | 'auto';
  align?: 'left' | 'center' | 'right';
  clickable?: boolean;
  block?: boolean;
  center?: boolean;
  borderRadius?: keyof typeof borderRadiusOptions;
  backgroundImage?: string;
  borderRadiusTop?: keyof typeof borderRadiusOptions;
  borderRadiusBottom?: keyof typeof borderRadiusOptions;
  borderRadiusLeft?: keyof typeof borderRadiusOptions;
  borderRadiusRight?: keyof typeof borderRadiusOptions;
  verticalAlign?: 'middle' | 'top';
  alignSelf?: 'flex-start' | 'center' | 'flex-end';
  display?: CssUnitsAsResponsiveObject<React.CSSProperties['display']>;
  flex?: React.CSSProperties['flex'];
  flexWrap?: CssUnitsAsResponsiveObject<'wrap' | 'nowrap' | 'wrap-reverse'>;
  flexGrow?: CssUnitsAsResponsiveObject<number>;
  flexShrink?: number;
  fixed?: boolean;
  cursor?: React.CSSProperties['cursor'];
  absoluteCenter?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  active?: boolean;
  maxWidth?: CssUnitsAsResponsiveObject<string | number>;
  minWidth?: CssUnitsAsResponsiveObject<string | number>;
  maxHeight?: CssUnitsAsResponsiveObject<string | number>;
  gap?: CssUnitsAsResponsiveObject<keyof typeof spacing>;
  rowGap?: keyof typeof spacing;
  height?: CssUnitsAsResponsiveObject<React.CSSProperties['height']>;
  width?: CssUnitsAsResponsiveObject<React.CSSProperties['width']>;
  justifyContent?: CssUnitsAsResponsiveObject<React.CSSProperties['justifyContent']>;
  overflow?: React.CSSProperties['overflow'];
  overflowX?: React.CSSProperties['overflowX'];
  overflowY?: React.CSSProperties['overflowY'];
  flexDirection?: CssUnitsAsResponsiveObject<React.CSSProperties['flexDirection']>;
  alignContent?: 'center' | 'flex-start' | 'flex-end';
  alignItems?: CssUnitsAsResponsiveObject<'center' | 'flex-start' | 'flex-end' | 'stretch' | 'baseline'>;
  children?: any;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  minHeight?: number | string;
  role?: React.AriaRole;
  onMouseEnter?: React.MouseEventHandler<HTMLElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLElement>;
  ref?: React.Ref<HTMLElement>;
  textDecoration?: 'none' | 'underline';
  type?: 'button';
  href?: LinkProps['href'];
  to?: LinkProps['to'];
  target?: React.HTMLAttributeAnchorTarget;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  tabIndex?: 0;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLElement>) => void;
  onTransitionEnd?: (e: React.TransitionEvent<HTMLDivElement>) => void;
  onAnimationStart?: React.AnimationEventHandler;
  onAnimationEnd?: React.AnimationEventHandler;
  transition?: React.CSSProperties['transition'];
  zIndex?: React.CSSProperties['zIndex'];
  /**
   * Optional ID for testing purposes
   */
  'data-testid'?: string | undefined;
  onWheel?: React.WheelEventHandler;
  onFocus?: React.FocusEventHandler<HTMLElement>;
  onBlur?: React.FocusEventHandler<HTMLElement>;
  onScroll?: React.UIEventHandler<HTMLElement>;
}

const BoxComponent = ({
  componentClass: TagName = 'div',
  className,
  block,
  absoluteCenter,
  disabled,
  style = {},
  active,
  ...rest
}: BoxProps) => {
  const generalClassNames = classNames({ block }, { active });

  const computedClassName = classNames(generalClassNames);

  return (
    <TagName
      className={classNames(
        'box-box',
        className,
        computedClassName,
        { 'absolute-center': absoluteCenter },
        { disabled },
      )}
      disabled={disabled}
      style={style}
      {...rest}
    />
  );
};

export const Box = styled(BoxComponent).withConfig({
  shouldForwardProp: prop =>
    !new Set([
      'overflow',
      'overflowY',
      'overflowX',
      'margin',
      'textDecoration',
      'marginBottom',
      'marginTop',
      'marginRight',
      'marginLeft',
      'marginVertical',
      'marginHorizontal',
      'paddingBottom',
      'padding',
      'paddingTop',
      'paddingLeft',
      'paddingRight',
      'paddingHorizontal',
      'paddingVertical',
      'flexDirection',
      'justifyContent',
      'alignContent',
      'clickable',
      'minWidth',
      'maxWidth',
      'maxHeight',
      'display',
      'flex',
      'flexGrow',
      'flexShrink',
      'flexWrap',
      'alignSelf',
      'alignItems',
      'position',
      'cursor',
      'height',
      'minHeight',
      'width',
      'verticalAlign',
      'right',
      'left',
      'top',
      'bottom',
      'gap',
      'rowGap',
      'transition',
      'borderRadius',
      'borderRadiusTop',
      'borderRadiusBottom',
      'borderRadiusLeft',
      'borderRadiusRight',
      'alignSelf',
      'center',
      'zIndex',
    ]).has(prop),
})<BoxProps>`
  &.box-box {
    ${props =>
      getCssRuleWithResponsiveCalculation<React.CSSProperties['display']>({
        property: 'display',
        value: props.display,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<React.CSSProperties['flexDirection']>({
        property: 'flex-direction',
        value: props.flexDirection,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<BoxProps['maxWidth']>({
        property: 'max-width',
        value: typeof props.maxWidth === 'number' ? `${props.maxWidth}px` : props.maxWidth,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<BoxProps['width']>({
        property: 'width',
        value: typeof props.width === 'number' ? `${props.width}px` : props.width,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<BoxProps['width']>({
        property: 'min-width',
        value: typeof props.minWidth === 'number' ? `${props.minWidth}px` : props.minWidth,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<BoxProps['maxHeight']>({
        property: 'max-height',
        value: typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : props.maxHeight,
      })};
    min-height: ${props => (typeof props.minHeight === 'number' ? `${props.minHeight}px` : props.minHeight)};
    ${props =>
      getCssRuleWithResponsiveCalculation<BoxProps['height']>({
        property: 'height',
        value: typeof props.height === 'number' ? `${props.height}px` : props.height,
      })};

    vertical-align: ${props => props.verticalAlign};
    overflow: ${props => props.overflow};
    overflow-y: ${props => props.overflowY};
    overflow-x: ${props => props.overflowX};
    ${props =>
      getCssRuleWithResponsiveCalculation<PositionProps['position']>({
        property: 'position',
        value: props.position,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<PositionProps['top']>({
        property: 'top',
        value: typeof props.top === 'number' ? `${props.top}px` : props.top,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<PositionProps['bottom']>({
        property: 'bottom',
        value: typeof props.bottom === 'number' ? `${props.bottom}px` : props.bottom,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<PositionProps['left']>({
        property: 'left',
        value: typeof props.left === 'number' ? `${props.left}px` : props.left,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<PositionProps['right']>({
        property: 'right',
        value: typeof props.right === 'number' ? `${props.right}px` : props.right,
      })};
    flex: ${props => props.flex};
    ${props =>
      getCssRuleWithResponsiveCalculation<BoxProps['flexGrow']>({
        property: 'flex-grow',
        value: props.flexGrow,
      })};
    text-decoration: ${props => props.textDecoration};
    ${props =>
      getCssRuleWithResponsiveCalculation({
        property: 'gap',
        value: mapSpacingPropsToValues(props.gap),
      })};
    row-gap: ${props => props.rowGap && spacing[props.rowGap]};
    flex-shrink: ${props => props.flexShrink};
    ${props =>
      getCssRuleWithResponsiveCalculation<BoxProps['flexWrap']>({
        property: 'flex-wrap',
        value: props.flexWrap,
      })};
    ${props =>
      getCssRuleWithResponsiveCalculation<React.CSSProperties['alignItems']>({
        property: 'align-items',
        value: props.alignItems,
      })};

    ${props =>
      getCssRuleWithResponsiveCalculation<BoxProps['justifyContent']>({
        property: 'justify-content',
        value: props.justifyContent,
      })};
    align-content: ${props => props.alignContent};
    z-index: ${props => props.zIndex};
    text-align: ${props => props.align};
    align-self: ${props => props.alignSelf};
    cursor: ${props => (props.clickable ? 'pointer' : props.cursor)};
    &[draggable='true'] {
      cursor: grab;
      &:active {
        cursor: grabbing;
      }
    }
    border-radius: ${props => props.borderRadius && borderRadiusOptions[props.borderRadius]?.size};
    ${props =>
      props.transition &&
      css`
        transition: ${props.transition};
      `};
    ${props =>
      props.borderRadiusTop &&
      css`
        border-top-left-radius: ${borderRadiusOptions[props.borderRadiusTop || 'none']?.size};
        border-top-right-radius: ${borderRadiusOptions[props.borderRadiusTop || 'none']?.size};
      `};

    ${props =>
      props.borderRadiusBottom &&
      css`
        border-bottom-left-radius: ${borderRadiusOptions[props.borderRadiusBottom || 'none']?.size};
        border-bottom-right-radius: ${borderRadiusOptions[props.borderRadiusBottom || 'none']?.size};
      `};

    ${props =>
      props.borderRadiusLeft &&
      css`
        border-bottom-left-radius: ${borderRadiusOptions[props.borderRadiusLeft || 'none']?.size};
        border-top-left-radius: ${borderRadiusOptions[props.borderRadiusLeft || 'none']?.size};
      `};

    ${props =>
      props.borderRadiusRight &&
      css`
        border-bottom-right-radius: ${borderRadiusOptions[props.borderRadiusRight || 'none']?.size};
        border-top-right-radius: ${borderRadiusOptions[props.borderRadiusRight || 'none']?.size};
      `};

    ${withSpacing};

    &.block {
      width: 100%;
    }

    z-index: ${props => props.zIndex};

    &.disabled * {
      cursor: not-allowed;
    }

    &.absolute-center {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;

export const Section = memo((props: BoxProps) => <Box componentClass={'section'} {...props} />);
export const Flex = memo((props: BoxProps) => <Box componentClass={'div'} display={'flex'} {...props} />);
export const Div = memo((props: BoxProps) => <Box componentClass={'div'} {...props} />);
export const Span = memo((props: BoxProps) => <Box componentClass={'span'} {...props} />);
export const Article = memo((props: BoxProps) => <Box componentClass={'article'} {...props} />);
export const Aside = memo((props: BoxProps) => <Box componentClass={'aside'} {...props} />);
export const Footer = memo((props: BoxProps) => <Box componentClass={'footer'} {...props} />);
export const Small = memo((props: BoxProps) => <Box componentClass={'small'} {...props} />);

Box.displayName = 'Box';
Section.displayName = 'Section';
Flex.displayName = 'Flex';
Div.displayName = 'Div';
Span.displayName = 'Span';
Article.displayName = 'Article';
Aside.displayName = 'Aside';
Footer.displayName = 'Footer';
Small.displayName = 'Small';
